import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../auth";
import { useLang } from "../lang";
import { LOGO_MAIN } from "../content";

function formatErr(detail) {
  if (!detail) return "Errore";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login, user } = useAuth();
  const { lang } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = (params.get("next") || "").startsWith("/") && !params.get("next").startsWith("//") ? params.get("next") : "/area-riservata";

  useEffect(() => {
    if (user) navigate(next);
  }, [user, navigate, next]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(lang === "it" ? "Accesso effettuato" : "Signed in");
      navigate(next);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || (lang === "it" ? "Credenziali non valide" : "Invalid credentials"));
    } finally { setLoading(false); }
  };

  return (
    <main className="min-h-screen bg-white flex items-center justify-center px-6 py-24" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
        className="w-full max-w-[420px]"
      >
        <Link to="/" className="flex items-center justify-center mb-8" data-testid="login-back-home">
          <img src={LOGO_MAIN} alt="HGW Industry" className="h-8" />
        </Link>

        <div className="text-center mb-8">
          <h1 className="text-[32px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">
            {lang === "it" ? "Area riservata" : "Reserved area"}
          </h1>
          <p className="mt-2 text-[15px] text-[#6e6e73]">
            {lang === "it"
              ? "Schede tecniche e strumenti riservati ai professionisti con Partita IVA."
              : "Datasheets and tools reserved for VAT-registered professionals."}
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-input-email"
            placeholder="Email"
            className="w-full bg-[#f5f5f7] rounded-xl px-4 py-3.5 text-[15px] text-[#1d1d1f] placeholder:text-[#6e6e73] outline-none focus:ring-2 focus:ring-[#1D3F94]/40"
          />
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-input-password"
            placeholder="Password"
            className="w-full bg-[#f5f5f7] rounded-xl px-4 py-3.5 text-[15px] text-[#1d1d1f] placeholder:text-[#6e6e73] outline-none focus:ring-2 focus:ring-[#1D3F94]/40"
          />
          <div className="text-right -mt-1">
            <Link to="/area-riservata/password-dimenticata" className="text-[13px] text-[#1D3F94] hover:underline" data-testid="login-forgot">
              {lang === "it" ? "Password dimenticata?" : "Forgot password?"}
            </Link>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="btn-pill btn-primary w-full justify-center disabled:opacity-60"
          >
            {loading ? "…" : (lang === "it" ? "Accedi" : "Sign in")}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-[#d2d2d7] text-center space-y-3">
          <div className="text-[13px] text-[#1d1d1f]">
            {lang === "it" ? "Non hai ancora un account?" : "No account yet?"}{" "}
            <Link to="/richiedi-accesso" className="text-[#1D3F94] font-medium hover:underline" data-testid="login-request-access">
              {lang === "it" ? "Richiedi l'accesso →" : "Request access →"}
            </Link>
          </div>
          <Link to="/" className="a-link inline-flex mt-2">← {lang === "it" ? "Torna al sito" : "Back to site"}</Link>
        </div>
      </motion.div>
    </main>
  );
}
